/**
 * Infinite Scroll for Shopify Collection Pages
 * Designed for Impulse v7.5.2 by Archetype Themes
 *
 * Progressive enhancement — degrades gracefully to standard pagination
 * when JavaScript is unavailable or the expected DOM isn't present.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Configuration
  // ---------------------------------------------------------------------------
  var CONFIG = {
    AUTO_LOAD_PAGES: 3,
    PREFETCH_ROOT_MARGIN: '0px 0px 200% 0px',
    LOAD_ROOT_MARGIN: '0px 0px 400px 0px',
    DOM_CLEANUP_THRESHOLD: 500,
    SCROLL_DEBOUNCE: 100,
    ANIMATION_DURATION: 500,
    STORAGE_KEY_PREFIX: 'infinite_scroll_',
    TOGGLE_STORAGE_KEY: 'infinite_scroll_enabled'
  };

  // ---------------------------------------------------------------------------
  // Utility helpers
  // ---------------------------------------------------------------------------
  function debounce(fn, wait) {
    var timer;
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(context, args); }, wait);
    };
  }

  function safeSessionGet(key) {
    try { return JSON.parse(sessionStorage.getItem(key)); } catch (e) { return null; }
  }

  function safeSessionSet(key, value) {
    try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* quota */ }
  }

  function safeSessionRemove(key) {
    try { sessionStorage.removeItem(key); } catch (e) { /* noop */ }
  }

  function safeLocalGet(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }

  function safeLocalSet(key, value) {
    try { localStorage.setItem(key, value); } catch (e) { /* noop */ }
  }

  // ---------------------------------------------------------------------------
  // InfiniteScroll Class
  // ---------------------------------------------------------------------------
  function InfiniteScroll(feed) {
    this.feed = feed;
    this.destroyed = false;

    // --- Read data attributes ---
    this.currentPage = parseInt(feed.dataset.currentPage, 10) || 1;
    this.totalPages = parseInt(feed.dataset.totalPages, 10) || 1;
    this.totalProducts = parseInt(feed.dataset.totalProducts, 10) || 0;
    this.perPage = parseInt(feed.dataset.productsPerPage, 10) || 24;
    this.sectionId = feed.dataset.sectionId || '';
    this.baseUrl = feed.dataset.baseUrl || '';

    // --- Schema settings (from data attributes) ---
    this.settings = {
      paginationType: feed.dataset.paginationType || 'infinite',
      autoLoadLimit: parseInt(feed.dataset.autoLoadLimit, 10) || CONFIG.AUTO_LOAD_PAGES,
      loadingStyle: feed.dataset.loadingStyle || 'skeleton',
      loadingText: feed.dataset.loadingText || 'Loading more products...',
      loadingTextColor: feed.dataset.loadingTextColor || '#666666',
      loadMoreText: feed.dataset.loadMoreText || 'Load More Products',
      loadMoreStyle: feed.dataset.loadMoreStyle || 'primary',
      showCountOnButton: feed.dataset.showCountOnButton !== 'false',
      showBackToTop: feed.dataset.showBackToTop !== 'false',
      backToTopAfter: parseInt(feed.dataset.backToTopAfter, 10) || 2,
      backToTopPosition: feed.dataset.backToTopPosition || 'bottom-right',
      backToTopThreshold: parseInt(feed.dataset.backToTopThreshold, 10) || 400,
      backToTopSize: feed.dataset.backToTopSize || 'medium',
      backToTopLabel: feed.dataset.backToTopLabel || 'Back to top',
      backToTopOffset: parseInt(feed.dataset.backToTopOffset, 10) || 20,
      showCounter: feed.dataset.showCounter !== 'false',
      counterStyle: feed.dataset.counterStyle || 'text',
      counterPosition: feed.dataset.counterPosition || 'below',
      urlMode: (function (v) {
        // Normalise legacy boolean strings and new 3-way value
        if (!v || v === 'false' || v === 'off') return 'off';
        if (v === 'hash') return 'hash';
        return 'query_param'; // 'true', 'query_param', anything else
      }(feed.dataset.urlUpdating)),
      scrollRestoration: feed.dataset.scrollRestoration !== 'false',
      ariaPosinset: feed.dataset.ariaPosinset !== 'false',
      ariaAnnouncements: feed.dataset.ariaAnnouncements !== 'false',
      showSkipLink: feed.dataset.showSkipLink !== 'false',
      prefetchEnabled: feed.dataset.prefetchEnabled !== 'false',
      lazyLoadImages: feed.dataset.lazyLoadImages !== 'false',
      contentVisibility: feed.dataset.contentVisibility !== 'false',
      endMessageText: feed.dataset.endMessageText || "You've seen all products",
      showEndDivider: feed.dataset.showEndDivider !== 'false'
    };

    // Derive collection handle from baseUrl (/collections/my-handle)
    var handleMatch = this.baseUrl.match(/\/collections\/([^/?#]+)/);
    this.collectionHandle = handleMatch ? handleMatch[1] : 'default';

    // --- Find sibling / parent elements ---
    this.wrapper = feed.closest('.collection-grid__wrapper');
    this.sectionContainer = feed.closest('.collection-content') || feed.closest('[data-section-type="collection-grid"]');
    this.sentinel = this.wrapper && this.wrapper.querySelector('[data-infinite-scroll-sentinel]');
    this.loadMoreBtn = this.wrapper && this.wrapper.querySelector('[data-infinite-scroll-load-more]');
    this.endMessage = this.wrapper && this.wrapper.querySelector('[data-infinite-scroll-end]');
    this.liveRegion = this.wrapper && this.wrapper.querySelector('[data-infinite-scroll-live]');
    this.counter = this.wrapper && this.wrapper.querySelector('[data-infinite-scroll-counter]');
    this.toggleCheckbox = this.wrapper && this.wrapper.querySelector('[data-infinite-scroll-toggle]');
    this.paginationEl = this.wrapper && this.wrapper.parentElement && this.wrapper.parentElement.querySelector('[data-infinite-scroll-pagination]');
    this.skeletonTemplate = this.wrapper && this.wrapper.querySelector('template[data-infinite-scroll-skeleton]');
    this.skipLink = this.wrapper && this.wrapper.querySelector('[data-infinite-scroll-skip]');
    this.progressBarContainer = this.wrapper && this.wrapper.querySelector('.infinite-scroll-progress');
    this.progressBar = this.progressBarContainer && this.progressBarContainer.querySelector('.infinite-scroll-progress__bar');
    this.backToTopBtn = this.wrapper && this.wrapper.querySelector('[data-infinite-scroll-back-to-top]');
    this.loadMoreCountSpan = this.loadMoreBtn && this.loadMoreBtn.querySelector('[data-load-more-count]');

    // --- Internal state ---
    this.loading = false;
    this.autoLoadCount = 0;
    this.prefetchCache = new Map();
    this.prefetchingPages = new Set();
    this.observers = [];          // all IntersectionObserver instances for cleanup
    this.eventListeners = [];     // all { target, event, handler } tuples for cleanup
    this.sentinelObserver = null;
    this.prefetchObserver = null;
    this.pageVisibilityObserver = null;
    this.placeholderObserver = null;
    this.enabled = true;
    this.lastReplaceStateUrl = '';
    this.loadedProductCount = this._countProducts();
    this.totalProductsSeen = this.loadedProductCount; // display counter (never decreases)
    this.highestPageLoaded = this.currentPage;
    this.visiblePage = this.currentPage;

    this._init();
  }

  // ---------------------------------------------------------------------------
  // Initialization
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._init = function () {
    // Standard pagination mode — don't initialize infinite scroll
    if (this.settings.paginationType === 'standard') {
      return;
    }

    // Single-page collection or missing elements — nothing to enhance
    if (this.totalPages <= 1 || !this.sentinel) {
      return;
    }

    // Mark section as active
    if (this.sectionContainer) {
      this.sectionContainer.classList.add('infinite-scroll-active');
    }

    // Manual scroll restoration so we control it
    if (this.settings.scrollRestoration) {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }
    }

    // Check user preference (toggle)
    var pref = safeLocalGet(CONFIG.TOGGLE_STORAGE_KEY);
    if (pref === 'false') {
      this.enabled = false;
    }

    if (this.toggleCheckbox) {
      this.toggleCheckbox.checked = this.enabled;
      this._on(this.toggleCheckbox, 'change', this._onToggleChange.bind(this));
    }

    if (this.enabled) {
      this._activate();
    } else {
      this._showPagination();
    }

    // --- Scroll-position restoration (back-button support) ---
    if (this.settings.scrollRestoration) {
      var self = this;
      this._on(window, 'pageshow', function (e) {
        if (e.persisted) {
          // Page restored from bfcache — everything is already in perfect state
          // Just re-attach any observers that may have been paused
          self._setupSentinelObserver();
          if (self.settings.prefetchEnabled) {
            self._setupPrefetchObserver();
          }
          return;
        }
        // Normal page load — attempt sessionStorage-based restoration
        self._attemptScrollRestore();
      });
    }

    // Intercept product link clicks to save state
    this._on(this.feed, 'click', this._onFeedClick.bind(this));
  };

  // ---------------------------------------------------------------------------
  // Activate / deactivate infinite scroll
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._activate = function () {
    this.autoLoadCount = 0;
    this._hidePagination();

    if (this.settings.paginationType === 'load_more') {
      // Load More mode — no auto-loading, just show button immediately
      if (this.highestPageLoaded < this.totalPages) {
        this._showLoadMore();
      }
    } else {
      // Infinite scroll mode — set up sentinel for auto-loading
      this._setupSentinelObserver();
    }

    this._setupPrefetchObserver();
    this._setupPageVisibilityObserver();
    this._updateCounter();
    this._updateProgress();

    this._setupBackToTop();

    if (this.loadMoreBtn && !this._loadMoreBound) {
      this._loadMoreBound = this._onLoadMoreClick.bind(this);
      this._on(this.loadMoreBtn, 'click', this._loadMoreBound);
    }
  };

  InfiniteScroll.prototype._deactivate = function () {
    this._disconnectObserver('sentinelObserver');
    this._disconnectObserver('prefetchObserver');
    this._disconnectObserver('pageVisibilityObserver');
    this._disconnectObserver('placeholderObserver');
    if (this.loadMoreBtn) this.loadMoreBtn.style.display = 'none';
    if (this.sentinel) this.sentinel.style.display = 'none';
  };

  // ---------------------------------------------------------------------------
  // Toggle handler
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._onToggleChange = function () {
    this.enabled = this.toggleCheckbox.checked;
    safeLocalSet(CONFIG.TOGGLE_STORAGE_KEY, String(this.enabled));

    if (this.enabled) {
      this._activate();
    } else {
      this._deactivate();
      this._showPagination();
    }
  };

  // ---------------------------------------------------------------------------
  // Intersection Observer — Sentinel (auto-load trigger)
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._setupSentinelObserver = function () {
    if (!this.sentinel || this.destroyed) return;

    // Clean up any existing sentinel observer
    this._disconnectObserver('sentinelObserver');

    var self = this;
    this.sentinelObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !self.loading && self.enabled) {
          self._loadNextPage();
        }
      });
    }, {
      rootMargin: CONFIG.LOAD_ROOT_MARGIN
    });

    this.sentinelObserver.observe(this.sentinel);
    this.observers.push(this.sentinelObserver);
    this.sentinel.style.display = '';
  };

  // ---------------------------------------------------------------------------
  // Intersection Observer — Prefetch (look-ahead)
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._setupPrefetchObserver = function () {
    if (!this.settings.prefetchEnabled) return;

    this._disconnectObserver('prefetchObserver');

    var self = this;
    this.prefetchObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var nextPage = self.highestPageLoaded + 1;
          if (nextPage <= self.totalPages && !self.prefetchCache.has(nextPage) && !self.loading && !self.prefetchingPages.has(nextPage)) {
            self.prefetchingPages.add(nextPage);
            var promise = self._fetchPage(nextPage).then(function (result) {
              if (result) self.prefetchCache.set(nextPage, result);
              return result;
            }).catch(function () { return null; }).finally(function () {
              self.prefetchingPages.delete(nextPage);
            });
            // Store the promise so _loadPage can await it instead of double-fetching
            self.prefetchPromises = self.prefetchPromises || new Map();
            self.prefetchPromises.set(nextPage, promise);
            promise.finally(function () { self.prefetchPromises.delete(nextPage); });
          }
        }
      });
    }, {
      rootMargin: CONFIG.PREFETCH_ROOT_MARGIN
    });

    this.observers.push(this.prefetchObserver);
    this._observeLastProduct();
  };

  InfiniteScroll.prototype._observeLastProduct = function () {
    if (!this.prefetchObserver) return;
    var products = this.feed.querySelectorAll('.grid-product');
    if (products.length === 0) return;
    this.prefetchObserver.disconnect();
    // Trigger prefetch at 70% through loaded products, not at the very last one
    var targetIndex = Math.max(0, Math.floor(products.length * 0.7) - 1);
    this.prefetchObserver.observe(products[targetIndex]);
  };

  // ---------------------------------------------------------------------------
  // Intersection Observer — Page visibility (URL management)
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._setupPageVisibilityObserver = function () {
    this._disconnectObserver('pageVisibilityObserver');

    var self = this;
    var updateUrl = debounce(function (pageNum) {
      self._replaceUrl(pageNum);
    }, CONFIG.SCROLL_DEBOUNCE);

    this.pageVisibilityObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var page = parseInt(entry.target.dataset.pageStart, 10);
          if (page) {
            self.visiblePage = page;
            updateUrl(page);
          }
        }
      });
    }, {
      threshold: 0.1
    });

    this.observers.push(this.pageVisibilityObserver);

    // Observe first product card of each loaded page
    var pageStarts = this.feed.querySelectorAll('[data-page-start]');
    pageStarts.forEach(function (el) {
      self.pageVisibilityObserver.observe(el);
    });
  };

  // ---------------------------------------------------------------------------
  // URL replacement via history.replaceState
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._replaceUrl = function (pageNum) {
    var mode = this.settings.urlMode;
    if (mode === 'off') return;

    var url = new URL(this.baseUrl, window.location.origin);
    var newPath;

    if (mode === 'hash') {
      // Hash fragment: /collections/shoes  or  /collections/shoes#page-3
      // Never touches the query string — zero server/Shopify impact
      newPath = url.pathname + (window.location.search || '') +
                (pageNum > 1 ? '#page-' + pageNum : '');
    } else {
      // query_param mode: /collections/shoes?page=3
      var params = new URLSearchParams(window.location.search);
      params.delete('section_id');
      params.delete('page');
      if (pageNum > 1) params.set('page', pageNum);
      var qs = params.toString();
      newPath = url.pathname + (qs ? '?' + qs : '');
    }

    if (newPath !== this.lastReplaceStateUrl) {
      this.lastReplaceStateUrl = newPath;
      history.replaceState({}, '', newPath);
    }
  };

  // ---------------------------------------------------------------------------
  // Page fetching via Section Rendering API
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._fetchPage = function (pageNum) {
    var self = this;
    var url = this._buildFetchUrl(pageNum);

    return fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var result = self._parseFetchedHtml(html, pageNum);

        // Prefetch images from the fetched page
        if (result && self.settings.prefetchEnabled) {
          try {
            var imgs = [];
            result.products.forEach(function (card) {
              card.querySelectorAll('img[src], img[data-src]').forEach(function (img) {
                var src = img.dataset.src || img.getAttribute('src');
                if (src && src.indexOf('cdn.shopify.com') !== -1) {
                  imgs.push(src);
                }
              });
            });
            // Preload first N images (don't preload all — just the ones likely to be seen)
            imgs.slice(0, 8).forEach(function (src) {
              var preloader = new Image();
              preloader.src = src;
            });
          } catch (e) { /* non-critical */ }
        }

        return result;
      });
  };

  InfiniteScroll.prototype._buildFetchUrl = function (pageNum) {
    var base = new URL(this.baseUrl, window.location.origin);
    var currentParams = new URLSearchParams(window.location.search);

    // Build clean params — keep filters/sort but not section_id or page
    var params = new URLSearchParams();
    currentParams.forEach(function (value, key) {
      var lower = key.toLowerCase();
      if (lower !== 'section_id' && lower !== 'page' && lower !== 'view') {
        params.append(key, value);
      }
    });

    params.set('section_id', this.sectionId);
    params.set('page', pageNum);

    return base.pathname + '?' + params.toString();
  };

  InfiniteScroll.prototype._parseFetchedHtml = function (html, pageNum) {
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');

    // Find the product grid inside #CollectionAjaxContent
    var ajaxContent = doc.getElementById('CollectionAjaxContent');
    var grid = ajaxContent
      ? ajaxContent.querySelector('.collection-product-grid')
      : doc.querySelector('.collection-product-grid');

    if (!grid) return null;

    // Extract product cards
    var products = Array.from(grid.querySelectorAll('.grid-product'));
    if (products.length === 0) return null;

    // Check if there's a "next" page link
    var nextLink = doc.querySelector('.pagination .next a[href], .pagination a.next[href]');
    // Fallback: look for any pagination next indicator
    if (!nextLink) {
      nextLink = doc.querySelector('[data-infinite-scroll-pagination] [rel="next"]');
    }

    return {
      products: products,
      hasNextPage: !!nextLink,
      pageNum: pageNum
    };
  };

  // ---------------------------------------------------------------------------
  // Load next page
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._loadNextPage = function () {
    var nextPage = this.highestPageLoaded + 1;

    if (nextPage > this.totalPages || this.loading || this.destroyed) return;

    // Check auto-load budget
    if (this.autoLoadCount >= this.settings.autoLoadLimit) {
      this._showLoadMore();
      return;
    }

    this._loadPage(nextPage, true);
  };

  InfiniteScroll.prototype._loadPage = function (pageNum, isAutoLoad) {
    if (this.loading || this.destroyed) return;

    var self = this;
    this.loading = true;
    this.feed.setAttribute('aria-busy', 'true');
    this._showSkeletonLoader();

    // Use prefetch cache if available, await in-flight prefetch, or fetch fresh
    var fetchPromise;
    if (this.prefetchCache.has(pageNum)) {
      fetchPromise = Promise.resolve(this.prefetchCache.get(pageNum));
      this.prefetchCache.delete(pageNum);
    } else if (this.prefetchPromises && this.prefetchPromises.has(pageNum)) {
      // Prefetch is in-flight — wait for it instead of double-fetching
      fetchPromise = this.prefetchPromises.get(pageNum).then(function () {
        if (self.prefetchCache.has(pageNum)) {
          var cached = self.prefetchCache.get(pageNum);
          self.prefetchCache.delete(pageNum);
          return cached;
        }
        return self._fetchPage(pageNum);
      });
    } else {
      fetchPromise = this._fetchPage(pageNum);
    }

    fetchPromise
      .then(function (result) {
        if (self.destroyed) return;
        if (!result) {
          self._showEndState();
          return;
        }

        self._insertPage(result);
        self.highestPageLoaded = pageNum;
        self.currentPage = pageNum;
        self.feed.dataset.currentPage = pageNum;

        if (isAutoLoad) {
          self.autoLoadCount++;
        }

        // Update state for whether we've reached the end
        if (pageNum >= self.totalPages || !result.hasNextPage) {
          self._showEndState();
        } else if (isAutoLoad && self.autoLoadCount >= self.settings.autoLoadLimit) {
          // Pause auto-loading, show manual button
          self._disconnectObserver('sentinelObserver');
          self._showLoadMore();
        }

        // Update prefetch observer to watch new last product
        self._observeLastProduct();
      })
      .catch(function (err) {
        if (self.destroyed) return;
        console.warn('[InfiniteScroll] Failed to load page ' + pageNum + ':', err);
        // On error, show Load More so user can retry
        self._showLoadMore();
      })
      .finally(function () {
        if (self.destroyed) return;
        self.loading = false;
        self.feed.setAttribute('aria-busy', 'false');
        self._hideSkeletonLoader();
      });
  };

  // ---------------------------------------------------------------------------
  // Content insertion
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._insertPage = function (result) {
    var products = result.products;
    var pageNum = result.pageNum;

    // Append all products directly into the first page's grid so they participate
    // in the same CSS Grid context and never create orphaned rows at page boundaries.
    var targetGrid = this.feed.querySelector('.collection-product-grid');
    if (!targetGrid) return;

    // Update aria attributes and lazy-load images, then append directly to grid
    var baseIndex = this.loadedProductCount;
    var fragment = document.createDocumentFragment();
    products.forEach(function (card, i) {
      // Mark first card of this page for URL scroll tracking
      if (i === 0) {
        card.dataset.pageStart = pageNum;
      }

      if (this.settings.ariaPosinset) {
        card.setAttribute('aria-posinset', baseIndex + i + 1);
        card.setAttribute('aria-setsize', this.totalProducts);
      }

      if (this.settings.lazyLoadImages) {
        card.querySelectorAll('img').forEach(function (img) {
          if (!img.hasAttribute('loading')) {
            img.setAttribute('loading', 'lazy');
          }
          // Enforce aspect-ratio to prevent CLS if width/height missing
          if (!img.hasAttribute('width') && !img.hasAttribute('height') &&
              !img.style.aspectRatio && !img.style.paddingBottom) {
            img.style.aspectRatio = '4 / 5';
          }
        });
      }

      fragment.appendChild(card);
    }.bind(this));
    targetGrid.appendChild(fragment);

    // Track loaded count
    this.loadedProductCount += products.length;
    this.totalProductsSeen += products.length;

    // Observe first product card for visibility (URL management)
    if (this.pageVisibilityObserver && products.length > 0) {
      this.pageVisibilityObserver.observe(products[0]);
    }

    // Reinitialize theme JS for new product cards (AOS handles animations)
    this._reinitTheme();

    // Update UI
    this._updateCounter();
    this._updateProgress();

    // Announce to screen readers
    this._announce(
      products.length + ' more products loaded, showing ' +
      this.totalProductsSeen + ' of ' + this.totalProducts
    );

    // DOM cleanup for very large collections
    this._checkDomCleanup();

    // Check back-to-top visibility
    this._checkBackToTopVisibility();
  };

  // ---------------------------------------------------------------------------
  // Theme re-initialization
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._reinitTheme = function () {
    if (this._suppressReinit) return;
    // Reinit product grid items (quick shop, swatches, etc.)
    if (typeof theme !== 'undefined' && typeof theme.reinitProductGridItem === 'function') {
      try { theme.reinitProductGridItem(); } catch (e) { /* safe */ }
    }

    // Refresh AOS animations
    if (typeof AOS !== 'undefined' && typeof AOS.refreshHard === 'function') {
      try { AOS.refreshHard(); } catch (e) { /* safe */ }
    }
  };

  // ---------------------------------------------------------------------------
  // Skeleton loader
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._showSkeletonLoader = function () {
    if (this.settings.loadingStyle === 'none') return;

    if (this.settings.loadingStyle === 'spinner') {
      this._showSpinner();
      return;
    }

    if (this.settings.loadingStyle === 'progress_bar') {
      this._showLoadingText();
      return;
    }

    // Default: skeleton cards — insert directly into the real grid
    if (!this.skeletonTemplate) return;

    var targetGrid = this.feed.querySelector('.collection-product-grid');
    if (!targetGrid) return;

    var clone = this.skeletonTemplate.content.cloneNode(true);
    var cards = clone.querySelectorAll('.infinite-scroll-skeleton-card');
    if (cards.length === 0) return;

    cards.forEach(function (card) {
      card.dataset.skeleton = 'true';
      targetGrid.appendChild(card);
    });
  };

  InfiniteScroll.prototype._hideSkeletonLoader = function () {
    var targetGrid = this.feed.querySelector('.collection-product-grid');
    if (targetGrid) {
      targetGrid.querySelectorAll('[data-skeleton="true"]').forEach(function (el) {
        el.parentNode.removeChild(el);
      });
    }
    // Also remove any old-style skeleton inserted outside the grid
    var oldSkeleton = this.feed.parentNode.querySelector('[data-active-skeleton]');
    if (oldSkeleton) oldSkeleton.parentNode.removeChild(oldSkeleton);
    this._hideSpinner();
    this._hideLoadingText();
  };

  // ---------------------------------------------------------------------------
  // Load More button
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._showLoadMore = function () {
    if (this.loadMoreBtn) {
      this.loadMoreBtn.style.display = '';
      this.loadMoreBtn.removeAttribute('hidden');
      // Update count on button
      if (this.settings.showCountOnButton && this.loadMoreCountSpan) {
        this.loadMoreCountSpan.textContent = ' (showing ' + this.totalProductsSeen + ' of ' + this.totalProducts + ')';
      }
    }
    if (this.sentinel) this.sentinel.style.display = 'none';
    this._disconnectObserver('sentinelObserver');
  };

  InfiniteScroll.prototype._hideLoadMore = function () {
    if (this.loadMoreBtn) {
      this.loadMoreBtn.style.display = 'none';
    }
  };

  InfiniteScroll.prototype._onLoadMoreClick = function (e) {
    e.preventDefault();
    this._hideLoadMore();
    this.autoLoadCount = 0; // Reset auto-load budget

    var self = this;
    var pageNum = this.highestPageLoaded + 1;

    // Intercept error to prevent sentinel auto-reconnect
    this.loading = true;
    this.feed.setAttribute('aria-busy', 'true');
    this._showSkeletonLoader();

    var fetchPromise;
    if (this.prefetchCache.has(pageNum)) {
      fetchPromise = Promise.resolve(this.prefetchCache.get(pageNum));
      this.prefetchCache.delete(pageNum);
    } else if (this.prefetchPromises && this.prefetchPromises.has(pageNum)) {
      fetchPromise = this.prefetchPromises.get(pageNum).then(function () {
        if (self.prefetchCache.has(pageNum)) {
          var cached = self.prefetchCache.get(pageNum);
          self.prefetchCache.delete(pageNum);
          return cached;
        }
        return self._fetchPage(pageNum);
      });
    } else {
      fetchPromise = this._fetchPage(pageNum);
    }

    fetchPromise
      .then(function (result) {
        if (self.destroyed) return;
        if (!result) {
          self._showEndState();
          return;
        }

        self._insertPage(result);
        self.highestPageLoaded = pageNum;
        self.currentPage = pageNum;
        self.feed.dataset.currentPage = pageNum;

        if (pageNum >= self.totalPages || !result.hasNextPage) {
          self._showEndState();
        } else {
          // Reconnect for next batch based on pagination type
          if (self.settings.paginationType === 'load_more') {
            self._showLoadMore();
          } else {
            // Reconnect sentinel for next batch of auto-loads
            self._setupSentinelObserver();
          }
        }

        self._observeLastProduct();
      })
      .catch(function (err) {
        if (self.destroyed) return;
        console.warn('[InfiniteScroll] Failed to load page ' + pageNum + ':', err);
        self._showLoadMore();
      })
      .finally(function () {
        if (self.destroyed) return;
        self.loading = false;
        self.feed.setAttribute('aria-busy', 'false');
        self._hideSkeletonLoader();
      });
  };

  // ---------------------------------------------------------------------------
  // End of content
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._showEndState = function () {
    this._disconnectObserver('sentinelObserver');
    this._hideLoadMore();

    if (this.sentinel) this.sentinel.style.display = 'none';

    if (this.endMessage) {
      this.endMessage.style.display = '';
      this.endMessage.removeAttribute('hidden');
      // Update total count spans within end message
      var countSpan = this.endMessage.querySelector('[data-count-total]');
      if (countSpan) countSpan.textContent = this.totalProducts;
    }

    this._updateCounter();
    this._updateProgress();
  };

  // ---------------------------------------------------------------------------
  // Counter & progress
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._updateCounter = function () {
    if (!this.counter) return;

    var currentSpan = this.counter.querySelector('[data-count-current]');
    var totalSpan = this.counter.querySelector('[data-count-total]');

    if (currentSpan) currentSpan.textContent = this.totalProductsSeen;
    if (totalSpan) totalSpan.textContent = this.totalProducts;
  };

  InfiniteScroll.prototype._updateProgress = function () {
    if (!this.progressBar) return;
    var pct = this.totalProducts > 0
      ? Math.min(100, Math.round((this.totalProductsSeen / this.totalProducts) * 100))
      : 0;
    this.progressBar.style.width = pct + '%';
    // ARIA attributes live on the parent container which has role="progressbar"
    if (this.progressBarContainer) {
      this.progressBarContainer.setAttribute('aria-valuenow', pct);
    }
  };

  // ---------------------------------------------------------------------------
  // Accessibility — live region announcements
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._announce = function (message) {
    if (!this.settings.ariaAnnouncements) return;
    if (!this.liveRegion) return;
    // Clear and re-set so screen readers pick up the change
    this.liveRegion.textContent = '';
    var region = this.liveRegion;
    requestAnimationFrame(function () {
      region.textContent = message;
    });
  };

  // ---------------------------------------------------------------------------
  // Pagination show/hide
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._hidePagination = function () {
    if (this.paginationEl) {
      this.paginationEl.setAttribute('aria-hidden', 'true');
      this.paginationEl.style.position = 'absolute';
      this.paginationEl.style.width = '1px';
      this.paginationEl.style.height = '1px';
      this.paginationEl.style.overflow = 'hidden';
      this.paginationEl.style.clip = 'rect(0,0,0,0)';
    }
  };

  InfiniteScroll.prototype._showPagination = function () {
    if (this.paginationEl) {
      this.paginationEl.removeAttribute('aria-hidden');
      this.paginationEl.style.position = '';
      this.paginationEl.style.width = '';
      this.paginationEl.style.height = '';
      this.paginationEl.style.overflow = '';
      this.paginationEl.style.clip = '';
    }
  };

  // ---------------------------------------------------------------------------
  // Scroll position restoration (back-button)
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._getStorageKey = function () {
    return CONFIG.STORAGE_KEY_PREFIX + 'state_' + this.collectionHandle;
  };

  InfiniteScroll.prototype._saveState = function (clickedEl) {
    if (!this.settings.scrollRestoration) return;
    var productHandle = null;
    if (clickedEl) {
      var card = clickedEl.closest('[data-product-handle]');
      if (card) productHandle = card.dataset.productHandle;
    }
    safeSessionSet(this._getStorageKey(), {
      url: location.href,
      scrollY: window.scrollY,
      currentPage: this.highestPageLoaded,
      productHandle: productHandle,
      timestamp: Date.now()
    });
  };

  InfiniteScroll.prototype._onFeedClick = function (e) {
    var link = e.target.closest('a[href]');
    if (link && link.href && !link.href.startsWith('#')) {
      this._saveState(e.target);
      // Set a return marker so "Shop Collection" links can also restore
      safeSessionSet('infinite_scroll_return_' + this.collectionHandle, { ts: Date.now() });
    }
  };

  InfiniteScroll.prototype._scrollToElement = function (target, callback) {
    var duration = 450;
    var block = 'center';

    // Respect prefers-reduced-motion
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      target.scrollIntoView({ block: block, behavior: 'auto' });
      if (callback) callback();
      return;
    }

    // Calculate target scroll position
    var rect = target.getBoundingClientRect();
    var startY = window.scrollY;
    var targetY = startY + rect.top - (window.innerHeight / 2) + (rect.height / 2);
    var maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    targetY = Math.max(0, Math.min(targetY, maxScroll));
    var distance = targetY - startY;

    if (Math.abs(distance) < 5) {
      // Already there
      if (callback) callback();
      return;
    }

    // easeInOutCubic: slow start, accelerates, eases in at destination
    function ease(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }

    var startTime = null;

    function step(now) {
      if (!startTime) startTime = now;
      var elapsed = now - startTime;
      var progress = Math.min(elapsed / duration, 1);
      window.scrollTo(0, startY + distance * ease(progress));
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        if (callback) callback();
      }
    }

    requestAnimationFrame(step);
  };

  InfiniteScroll.prototype._attemptScrollRestore = function () {
    if (!this.settings.scrollRestoration) return;

    // Only restore on browser back/forward navigation, not refresh or direct load
    var navType = '';
    try {
      var navEntries = performance.getEntriesByType('navigation');
      if (navEntries && navEntries.length > 0) {
        navType = navEntries[0].type; // 'navigate', 'reload', 'back_forward', 'prerender'
      } else if (performance.navigation) {
        // Fallback for older browsers
        navType = performance.navigation.type === 2 ? 'back_forward' : 'navigate';
      }
    } catch (e) { /* safe fallback */ }

    var isBackForward = !navType || navType === 'back_forward';

    // Also allow restoration when user clicks "Shop Collection" from a product page
    var isCollectionReturn = false;
    var returnMarker = safeSessionGet('infinite_scroll_return_' + this.collectionHandle);
    if (returnMarker && returnMarker.ts && (Date.now() - returnMarker.ts) < 30000) {
      // Marker is fresh — also check referrer is a product page on same host
      try {
        var ref = document.referrer;
        if (ref) {
          var refUrl = new URL(ref);
          if (refUrl.hostname === location.hostname && refUrl.pathname.indexOf('/products/') !== -1) {
            isCollectionReturn = true;
          }
        }
      } catch (e) { /* ignore */ }
    }
    safeSessionRemove('infinite_scroll_return_' + this.collectionHandle);

    if (!isBackForward && !isCollectionReturn) {
      // Refresh or direct navigation — clear any stale state and abort
      safeSessionRemove(this._getStorageKey());
      return;
    }

    var stored = safeSessionGet(this._getStorageKey());
    if (!stored || !stored.currentPage || stored.currentPage <= 1) return;

    // Expire after 30 minutes
    if (stored.timestamp && (Date.now() - stored.timestamp) > 1800000) {
      safeSessionRemove(this._getStorageKey());
      return;
    }

    // Only restore if we're on the right collection
    var currentPath = window.location.pathname;
    if (stored.url) {
      try {
        var storedPath = new URL(stored.url, window.location.origin).pathname;
        if (storedPath !== currentPath) {
          safeSessionRemove(this._getStorageKey());
          return;
        }
      } catch (e) {
        return;
      }
    }

    var targetPage = stored.currentPage;
    var targetScrollY = stored.scrollY || 0;
    var self = this;

    // Clear stored state immediately so we don't re-restore
    safeSessionRemove(this._getStorageKey());

    // Disconnect sentinel during rapid loading
    this._disconnectObserver('sentinelObserver');

    // Build array of pages to load (2 through targetPage)
    var pagesToLoad = [];
    for (var p = 2; p <= targetPage; p++) {
      pagesToLoad.push(p);
    }

    if (pagesToLoad.length === 0) return;

    // Show loading state
    this.loading = true;
    this.feed.setAttribute('aria-busy', 'true');

    // Show restoring indicator
    var restoringEl = document.createElement('div');
    restoringEl.className = 'infinite-scroll-restoring';
    restoringEl.setAttribute('role', 'status');
    restoringEl.setAttribute('aria-live', 'polite');
    restoringEl.textContent = 'Restoring your position\u2026';
    self.feed.parentNode.insertBefore(restoringEl, self.feed);

    // Fetch all pages in parallel
    Promise.all(pagesToLoad.map(function (num) {
      return self._fetchPage(num).catch(function () { return null; });
    }))
    .then(function (results) {
      if (self.destroyed) return;

      // Insert pages in order (suppress per-page reinit, batch at end)
      self._suppressReinit = true;
      results.forEach(function (result) {
        if (result) {
          self._insertPage(result);
          self.highestPageLoaded = Math.max(self.highestPageLoaded, result.pageNum);
          self.currentPage = self.highestPageLoaded;
          self.feed.dataset.currentPage = self.highestPageLoaded;
        }
      });
      self._suppressReinit = false;
      self._reinitTheme();

      // Restore scroll position after DOM settles
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          // Remove restoring indicator
          var indicator = self.feed.parentNode.querySelector('.infinite-scroll-restoring');
          if (indicator) indicator.parentNode.removeChild(indicator);

          // Prefer product handle anchoring over Y-coordinate
          var scrolled = false;
          if (stored.productHandle) {
            var target = self.feed.querySelector('[data-product-handle="' + stored.productHandle + '"]');
            if (target) {
              self._scrollToElement(target, function () {
                target.classList.add('infinite-scroll-restored');
                setTimeout(function () {
                  target.classList.remove('infinite-scroll-restored');
                }, 1500);
              });
              scrolled = true;
            }
          }

          // Fallback to Y-coordinate if no handle or product not found
          if (!scrolled && stored.scrollY) {
            window.scrollTo(0, stored.scrollY);
          }

          // Reconnect observers
          if (self.highestPageLoaded >= self.totalPages) {
            self._showEndState();
          } else {
            self.autoLoadCount = 0;
            self._setupSentinelObserver();
            self._observeLastProduct();
          }
        });
      });
    })
    .catch(function (err) {
      console.warn('[InfiniteScroll] Scroll restore failed:', err);
    })
    .finally(function () {
      self.loading = false;
      self.feed.setAttribute('aria-busy', 'false');
      // Always remove restoring indicator (error path — success path removes it inside rAF)
      if (restoringEl && restoringEl.parentNode) {
        restoringEl.parentNode.removeChild(restoringEl);
      }
    });
  };

  // ---------------------------------------------------------------------------
  // DOM cleanup for large collections
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._checkDomCleanup = function () {
    if (this.loadedProductCount < CONFIG.DOM_CLEANUP_THRESHOLD) return;

    var targetGrid = this.feed.querySelector('.collection-product-grid');
    if (!targetGrid) return;

    // First product cards with data-page-start mark each loaded page boundary.
    // Each owns itself and all grid siblings until the next [data-page-start].
    var markers = Array.from(
      targetGrid.querySelectorAll('[data-page-start]')
    );
    if (markers.length < 4) return;

    var self = this;
    var viewportCenter = window.innerHeight / 2 + window.scrollY;
    var toVirtualize = [];

    // PASS 1: All layout reads (no DOM writes)
    markers.forEach(function (marker) {
      // Skip if already a placeholder
      if (marker.classList && marker.classList.contains('infinite-scroll-placeholder')) return;

      var products = [marker];
      var sibling = marker.nextElementSibling;
      while (sibling && !sibling.hasAttribute('data-page-start') &&
             !sibling.classList.contains('infinite-scroll-placeholder')) {
        products.push(sibling);
        sibling = sibling.nextElementSibling;
      }

      if (products.length === 0) return;

      var firstRect = products[0].getBoundingClientRect();
      var lastRect = products[products.length - 1].getBoundingClientRect();
      var groupCenter = (firstRect.top + lastRect.bottom) / 2 + window.scrollY;
      var groupHeight = lastRect.bottom - firstRect.top;

      if (Math.abs(viewportCenter - groupCenter) > window.innerHeight * 3) {
        toVirtualize.push({ marker: marker, products: products, height: groupHeight });
      }
    });

    // PASS 2: All DOM writes (after all reads complete)
    toVirtualize.forEach(function (item) {
      self._virtualizePage(item.marker, item.products, item.height);
    });
  };

  InfiniteScroll.prototype._virtualizePage = function (marker, products, height) {
    var pageNum = marker.dataset.pageStart;
    if (!pageNum || !marker.parentNode) return;

    // Grid-spanning placeholder maintains row height while content is removed
    var placeholder = document.createElement('div');
    placeholder.className = 'infinite-scroll-page infinite-scroll-placeholder';
    placeholder.dataset.placeholderPage = pageNum;
    placeholder.dataset.page = pageNum;
    placeholder.style.gridColumn = '1 / -1';
    placeholder.style.height = height + 'px';
    placeholder.setAttribute('aria-hidden', 'true');

    // Insert placeholder before the first product (marker), then remove all products
    marker.parentNode.insertBefore(placeholder, marker);

    var productCount = products.length;
    products.forEach(function (p) {
      if (p.parentNode) p.parentNode.removeChild(p);
    });

    this.loadedProductCount -= productCount;

    // Update UI to reflect virtualized state
    this._updateCounter();
    this._updateProgress();

    // Observe placeholder to restore when user scrolls near it
    this._observePlaceholder(placeholder);
  };

  InfiniteScroll.prototype._observePlaceholder = function (placeholder) {
    var self = this;

    if (!this.placeholderObserver) {
      this.placeholderObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            self._restorePlaceholder(entry.target);
          }
        });
      }, {
        rootMargin: '200% 0px 200% 0px'
      });
      this.observers.push(this.placeholderObserver);
    }

    this.placeholderObserver.observe(placeholder);
  };

  InfiniteScroll.prototype._restorePlaceholder = function (placeholder) {
    var pageNum = parseInt(placeholder.dataset.placeholderPage, 10);
    if (!pageNum || this.destroyed) return;

    if (this.placeholderObserver) {
      this.placeholderObserver.unobserve(placeholder);
    }

    var self = this;
    this._fetchPage(pageNum)
      .then(function (result) {
        if (!result || self.destroyed || !placeholder.parentNode) return;

        // Mark first restored product as the page boundary for URL tracking
        result.products[0].dataset.pageStart = pageNum;

        result.products.forEach(function (card) {
          if (self.settings.lazyLoadImages) {
            card.querySelectorAll('img:not([loading])').forEach(function (img) {
              img.setAttribute('loading', 'lazy');
            });
          }
          placeholder.parentNode.insertBefore(card, placeholder);
        });

        placeholder.parentNode.removeChild(placeholder);

        self.loadedProductCount += result.products.length;
        self._reinitTheme();
        self._updateCounter();
        self._updateProgress();

        // Observe restored first product card for visibility tracking
        if (self.pageVisibilityObserver && result.products.length > 0) {
          self.pageVisibilityObserver.observe(result.products[0]);
        }
      })
      .catch(function () {
        // If restore fails, leave placeholder
      });
  };

  // ---------------------------------------------------------------------------
  // Spinner loader (alternative to skeleton)
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._showSpinner = function () {
    if (this._spinnerEl) return;
    this._spinnerEl = document.createElement('div');
    this._spinnerEl.className = 'infinite-scroll-spinner';
    this._spinnerEl.setAttribute('aria-hidden', 'true');
    var text = this.settings.loadingText;
    if (text) {
      var textEl = document.createElement('span');
      textEl.className = 'infinite-scroll-spinner__text';
      textEl.textContent = text;
      if (this.settings.loadingTextColor) {
        textEl.style.color = this.settings.loadingTextColor;
      }
      this._spinnerEl.appendChild(textEl);
    }
    this.feed.parentNode.insertBefore(this._spinnerEl, this.sentinel);
  };

  InfiniteScroll.prototype._hideSpinner = function () {
    if (this._spinnerEl && this._spinnerEl.parentNode) {
      this._spinnerEl.parentNode.removeChild(this._spinnerEl);
      this._spinnerEl = null;
    }
  };

  // ---------------------------------------------------------------------------
  // Loading text (alternative to skeleton)
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._showLoadingText = function () {
    if (this._loadingTextEl) return;
    this._loadingTextEl = document.createElement('div');
    this._loadingTextEl.className = 'infinite-scroll-loading-text';
    this._loadingTextEl.textContent = this.settings.loadingText;
    if (this.settings.loadingTextColor) {
      this._loadingTextEl.style.color = this.settings.loadingTextColor;
    }
    this.feed.parentNode.insertBefore(this._loadingTextEl, this.sentinel);
  };

  InfiniteScroll.prototype._hideLoadingText = function () {
    if (this._loadingTextEl && this._loadingTextEl.parentNode) {
      this._loadingTextEl.parentNode.removeChild(this._loadingTextEl);
      this._loadingTextEl = null;
    }
  };

  // ---------------------------------------------------------------------------
  // Back-to-Top button
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._setupBackToTop = function () {
    if (!this.settings.showBackToTop || !this.backToTopBtn) return;

    // Apply configurable label, size, and offset
    this.backToTopBtn.setAttribute('aria-label', this.settings.backToTopLabel);
    this.backToTopBtn.dataset.size = this.settings.backToTopSize;
    this.backToTopBtn.style.setProperty('--btt-offset', this.settings.backToTopOffset + 'px');

    // Scroll to top on click
    this._on(this.backToTopBtn, 'click', function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    // Re-evaluate visibility on scroll so button hides when user scrolls back to top
    var self = this;
    this._on(window, 'scroll', debounce(function () {
      self._checkBackToTopVisibility();
    }, 150));
  };

  InfiniteScroll.prototype._checkBackToTopVisibility = function () {
    if (!this.settings.showBackToTop || !this.backToTopBtn) return;

    var enoughPagesLoaded = this.highestPageLoaded >= this.settings.backToTopAfter;
    var scrolledDown = window.scrollY > this.settings.backToTopThreshold;
    var shouldShow = enoughPagesLoaded && scrolledDown;
    this.backToTopBtn.classList.toggle('is-visible', shouldShow);
    this.backToTopBtn.style.display = shouldShow ? '' : 'none';
  };

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype._countProducts = function () {
    return this.feed.querySelectorAll('.grid-product').length;
  };

  /** Register an event listener for cleanup */
  InfiniteScroll.prototype._on = function (target, event, handler) {
    target.addEventListener(event, handler);
    this.eventListeners.push({ target: target, event: event, handler: handler });
  };

  /** Disconnect a named observer and remove from tracking array */
  InfiniteScroll.prototype._disconnectObserver = function (name) {
    if (this[name]) {
      this[name].disconnect();
      var idx = this.observers.indexOf(this[name]);
      if (idx > -1) this.observers.splice(idx, 1);
      this[name] = null;
    }
  };

  // ---------------------------------------------------------------------------
  // Destroy — full cleanup
  // ---------------------------------------------------------------------------
  InfiniteScroll.prototype.destroy = function () {
    this.destroyed = true;

    // Disconnect all observers
    this.observers.forEach(function (obs) {
      try { obs.disconnect(); } catch (e) { /* safe */ }
    });
    this.observers = [];
    this.sentinelObserver = null;
    this.prefetchObserver = null;
    this.pageVisibilityObserver = null;
    this.placeholderObserver = null;

    // Remove all event listeners
    this.eventListeners.forEach(function (entry) {
      try { entry.target.removeEventListener(entry.event, entry.handler); } catch (e) { /* safe */ }
    });
    this.eventListeners = [];

    // Clear prefetch cache and in-flight promises
    this.prefetchCache.clear();
    this.prefetchingPages.clear();
    if (this.prefetchPromises) this.prefetchPromises.clear();

    // Restore scroll restoration to browser default
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'auto';
    }

    // Remove active class
    if (this.sectionContainer) {
      this.sectionContainer.classList.remove('infinite-scroll-active');
    }
  };

  // ---------------------------------------------------------------------------
  // Auto-initialization
  // ---------------------------------------------------------------------------
  function initInfiniteScroll() {
    var container = document.querySelector('[data-infinite-scroll-feed]');
    if (!container) return;

    // Destroy existing instance if present
    if (window.__infiniteScroll) {
      window.__infiniteScroll.destroy();
      window.__infiniteScroll = null;
    }

    window.__infiniteScroll = new InfiniteScroll(container);
  }

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInfiniteScroll);
  } else {
    initInfiniteScroll();
  }

  // Re-initialize after filter/sort AJAX changes replace #CollectionAjaxContent
  document.addEventListener('collection:reloaded', function () {
    // Double rAF ensures DOM is fully settled after theme's AJAX replacement
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        initInfiniteScroll();
      });
    });
  });
})();
